/**
 * Coups de pouce — Attendu 900 « Connaître le sens et les situations d'emploi
 * des opérations ». Une entrée par generator_key (cf. gen_900.js), deux fiches
 * de deux pages : « choisir » (cartes choisir_operation et associer_calcul) et
 * « question » (carte question_resoluble). Campagne 5e (2026-09-21, spec
 * 900.json) : les cartes repartir et fois_div ont été supprimées du gen, leurs
 * fiches avec elles ; la fiche « associer » (répétitions, moitié) ne
 * correspondait plus à la carte recalibrée et a été retirée.
 *
 * ─── Deux retours utilisateur, deux refontes ───────────────────────────────
 *
 * 1ʳᵉ version : tout en SVG, la règle écrite en <text> DANS la figure.
 *   → « pas top pour qu'un élève comprenne comment faire ». Trois causes :
 *     la sheet ne lit à voix haute que `court` et `lignes`, donc une fiche
 *     100 % SVG était MUETTE ; la règle était énoncée mais jamais la marche à
 *     suivre ; et 3 à 4 lignes de texte en 12 px dans 290 px de large sont
 *     illisibles sur téléphone.
 *
 * 2ᵉ version : `court` + trois `lignes` par page.
 *   → « trop de texte, fais plus synthétique » et « retravaille les SVG pour
 *     que ça corresponde à des cas de l'exo ». D'où l'état actuel :
 *
 *   `court`  → l'idée-clé, une phrase courte (rendue en gras avec 💡)
 *   `lignes` → DEUX étapes, numérotées par la sheet
 *   `svg`    → le schéma seul, sans texte de règle
 * Soit environ la moitié du texte de la version précédente. `court` + `lignes`
 * sont lus à voix haute ; le SVG garde son `aria-label`.
 *
 * ─── Les figures montrent des situations DE L'EXERCICE ────────────────────
 * Plus de schéma abstrait : chaque scène est une situation que le générateur
 * tire vraiment (ruban découpé, briques, carte de cantine, tirelire, livre lu
 * soir après soir). Une correction de fond au passage :
 *   - `question_resoluble` parlait de billes ; ses familles réelles sont
 *     peinture, trajet et lecture. La fiche prend la lecture.
 *
 * Les VALEURS, elles, restent hors des plages de tirage, de sorte qu'une fiche
 * ne serve jamais la réponse de l'exercice ouvert :
 *   - choisir : 90 cm en 9 morceaux (le ruban ne dépasse pas 86,4 cm et se
 *     coupe en 8 morceaux au plus), 10 briques (9 au plus), 45 € sur la carte
 *     de cantine (16,65 € au plus), 80 € et 20 € de plus (25,65 € au plus),
 *     50 € et 12 € de moins (24,95 € au plus) ;
 *   - question : 60 pages par jour pendant 30 jours (le gen tire p ≤ 12 pages
 *     par soir et j ≤ 16 soirs).
 *
 * Trois invariants d'animation repris de la revue 899 :
 *   - l'animation se FIGE une fois terminée (pas de boucle),
 *   - un bouton « ↻ Revoir » la relance,
 *   - la dernière étape à apparaître est la conclusion du schéma.
 * D'où SMIL (<animate>) plutôt que des @keyframes CSS : le SVG est injecté par
 * dangerouslySetInnerHTML, aucun JS ne s'y exécute — seul `begin="0s;btn.click"`
 * permet un rejeu et `fill="freeze"` un arrêt sur la dernière image.
 *
 * ⚠ Géométrie : les dix figures sont passées à une sonde qui reconstitue la
 * boîte de chaque <text> (~0,55 em par glyphe) et vérifie qu'elle tient dans le
 * viewBox, ne recouvre ni le bouton « Revoir » ni un autre texte, et ne déborde
 * d'aucun rectangle qui ne la contient pas. Toute retouche est à re-mesurer.
 */

/**
 * Étape d'apparition : le groupe reste transparent jusqu'à `p` (fraction du
 * cycle), monte en 3 % puis GÈLE. Tous les groupes d'une figure partagent le
 * même `begin`, donc un clic sur le bouton rejoue toute la cascade.
 */
const apparition = (p, btn, duree = '7.6s') =>
    `<animate attributeName="opacity" values="0;0;1;1" ` +
    `keyTimes="0;${p.toFixed(2)};${(p + 0.03).toFixed(2)};1" ` +
    `dur="${duree}" begin="0s;${btn}.click" fill="freeze"/>`;

/** Groupe animé. opacity="1" en attribut = repli si SMIL est indisponible. */
const etape = (p, btn, contenu) =>
    `<g class="anim" opacity="1">${apparition(p, btn)}${contenu}</g>`;

const bouton = (id, x, y) =>
    `<g id="${id}" style="cursor:pointer">` +
    `<title>Rejouer l'animation</title>` +
    `<rect x="${x}" y="${y}" width="76" height="21" rx="10.5" fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.2"/>` +
    `<text x="${x + 38}" y="${y + 15}" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E3A8A">↻ Revoir</text>` +
    `</g>`;

// Le seul CSS restant : respecter prefers-reduced-motion. En cas d'échec de la
// règle, l'élève voit l'animation, jamais une figure vide.
const STYLE_REDUCED = `<style>@media (prefers-reduced-motion: reduce) { .anim { opacity: 1 !important; } }</style>`;

const BLEU = '#1E3A8A', ORANGE = '#f97316', ARDOISE = '#334155', ENCRE = '#0f172a';
const GRIS = '#94a3b8';

const barre = (x, y, w, h, fill, stroke) =>
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="3" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>`;

/** Filet de séparation entre les deux scènes d'une même figure. */
const filet = (y) => `<line x1="14" y1="${y}" x2="286" y2="${y}" stroke="#e2e8f0" stroke-width="1.2"/>`;

// ═══════════════════════════════════════════════════════════
// Fiche 1 — Choisir la bonne opération
// ═══════════════════════════════════════════════════════════

const BTN_A = 'cp900rejouerA', BTN_B = 'cp900rejouerB';

/**
 * Une situation de l'exercice sur une bande de 272 px : le symbole à gauche,
 * la situation en haut, le calcul dessous.
 */
const bandeCas = (y, symbole, situation, calcul) =>
    `<rect x="14" y="${y}" width="272" height="42" rx="7" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.2"/>` +
    `<text x="32" y="${y + 28}" text-anchor="middle" font-size="20" font-weight="bold" fill="${BLEU}">${symbole}</text>` +
    `<line x1="50" y1="${y + 6}" x2="50" y2="${y + 36}" stroke="#e2e8f0" stroke-width="1.2"/>` +
    `<text x="60" y="${y + 18}" font-size="10.5" fill="${ARDOISE}">${situation}</text>` +
    `<text x="60" y="${y + 34}" font-size="11" font-weight="bold" fill="${BLEU}">${calcul}</text>`;

const SVG_QUATRE_CAS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 226" width="290" height="219" role="img"
     aria-label="Quatre situations apparaissent l'une après l'autre, chacune avec son opération. Division : 90 centimètres de ruban en 9 morceaux égaux, 90 divisé par 9 égale 10 centimètres par morceau. Multiplication : 10 briques identiques de 6 kilogrammes, 6 multiplié par 10 égale 60 kilogrammes. Soustraction : 45 euros sur la carte de cantine, il reste 12 euros après le repas, 45 moins 12 égale 33 euros pour le repas. Addition : un livre payé 50 euros, soit 12 euros de moins que Tom, 50 plus 12 égale 62 euros pour Tom. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="19" font-size="13" font-weight="bold" fill="${ENCRE}">Quatre situations</text>
  ${bouton(BTN_A, 210, 4)}
  ${etape(0.06, BTN_A, bandeCas(30, '÷', '90 cm de ruban en 9 morceaux égaux', '90 ÷ 9 = 10 cm par morceau'))}
  ${etape(0.24, BTN_A, bandeCas(78, '×', '10 briques identiques de 6 kg', '6 × 10 = 60 kg'))}
  ${etape(0.42, BTN_A, bandeCas(126, '−', '45 € sur la carte, il reste 12 € après', '45 − 12 = 33 € le repas'))}
  ${etape(0.60, BTN_A, bandeCas(174, '+', 'livre payé 50 €, 12 € de moins que Tom', '50 + 12 = 62 € pour Tom'))}
</svg>`.trim();

// ─── Page 2 — les deux pièges additifs, sur la tirelire de l'exercice ──────
// 80 € et 20 €. « de plus que » → Tom a 60 ; « de moins que » → Tom a 100.
// Mêmes nombres, seul le mot change : c'est tout le propos.
const PB_X = 48, PX_TIRELIRE = 2;           // 80 € sur 160 px
const W80 = 80 * PX_TIRELIRE, W20 = 20 * PX_TIRELIRE;

const SVG_PLUS_MOINS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 206" width="290" height="199" role="img"
     aria-label="Deux situations avec les mêmes nombres. En haut, Léa a 80 euros dans sa tirelire, soit 20 euros de plus que Tom : la barre de Léa se termine par un morceau orange de 20 euros que celle de Tom n'a pas, Tom a donc moins, et 80 moins 20 égale 60 euros. En bas, Léa a 80 euros, soit 20 euros de moins que Tom : cette fois c'est la barre de Tom qui dépasse du morceau orange, Tom a donc plus, et 80 plus 20 égale 100 euros. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${bouton(BTN_B, 210, 176)}
  ${etape(0.04, BTN_B,
    `<text x="14" y="18" font-size="11.5" font-weight="bold" fill="${ENCRE}">Léa a 80 €, soit 20 € <tspan fill="${ORANGE}">de plus</tspan> que Tom</text>` +
    `<text x="14" y="41" font-size="11" font-weight="bold" fill="${ARDOISE}">Léa</text>` +
    barre(PB_X, 28, W80 - W20, 17, '#dbeafe', BLEU))}
  ${etape(0.16, BTN_B,
    barre(PB_X + W80 - W20, 28, W20, 17, '#ffedd5', ORANGE) +
    `<text x="${PB_X + W80 - W20 / 2}" y="41" text-anchor="middle" font-size="9.5" font-weight="bold" fill="${ORANGE}">20 €</text>` +
    `<text x="${PB_X + W80 + 8}" y="41" font-size="11" font-weight="bold" fill="${BLEU}">80 €</text>`)}
  ${etape(0.28, BTN_B,
    `<text x="14" y="65" font-size="11" font-weight="bold" fill="${ARDOISE}">Tom</text>` +
    barre(PB_X, 52, W80 - W20, 17, '#e2e8f0', GRIS) +
    `<text x="${PB_X + W80 - W20 + 8}" y="65" font-size="11" font-weight="bold" fill="${ARDOISE}">?</text>`)}
  ${etape(0.40, BTN_B, `<text x="14" y="90" font-size="13.5" font-weight="bold" fill="${ENCRE}">80 − 20 = <tspan fill="${BLEU}">60 €</tspan></text>`)}
  ${etape(0.50, BTN_B, filet(102))}
  ${etape(0.54, BTN_B,
    `<text x="14" y="124" font-size="11.5" font-weight="bold" fill="${ENCRE}">Léa a 80 €, soit 20 € <tspan fill="${ORANGE}">de moins</tspan> que Tom</text>` +
    `<text x="14" y="147" font-size="11" font-weight="bold" fill="${ARDOISE}">Léa</text>` +
    barre(PB_X, 134, W80, 17, '#dbeafe', BLEU) +
    `<text x="${PB_X + W80 + 8}" y="147" font-size="11" font-weight="bold" fill="${BLEU}">80 €</text>`)}
  ${etape(0.68, BTN_B,
    `<text x="14" y="171" font-size="11" font-weight="bold" fill="${ARDOISE}">Tom</text>` +
    barre(PB_X, 158, W80, 17, '#e2e8f0', GRIS) +
    barre(PB_X + W80, 158, W20, 17, '#ffedd5', ORANGE) +
    `<text x="${PB_X + W80 + W20 / 2}" y="171" text-anchor="middle" font-size="9.5" font-weight="bold" fill="${ORANGE}">20 €</text>` +
    `<text x="${PB_X + W80 + W20 + 8}" y="171" font-size="11" font-weight="bold" fill="${ARDOISE}">?</text>`)}
  ${etape(0.84, BTN_B, `<text x="14" y="196" font-size="13.5" font-weight="bold" fill="${ENCRE}">80 + 20 = <tspan fill="${ORANGE}">100 €</tspan></text>`)}
</svg>`.trim();

/** Fiche « choisir » : le geste de chaque opération, puis les pièges additifs. */
const FICHE_CHOISIR = {
    pages: [
        {
            titre: 'Quelle opération ?',
            court: 'Regarde ce que tu cherches, pas les mots de l\'énoncé.',
            lignes: [
                'Je partage en parts égales : **÷**. Je répète une même quantité : **×**.',
                'Je réunis deux morceaux : **+**. Je retire, ou je compare : **−**.',
            ],
            svg: SVG_QUATRE_CAS,
        },
        {
            titre: 'Les pièges « de plus » et « de moins »',
            court: '« de plus que » ne veut pas dire +.',
            lignes: [
                'Cherche **qui en a le plus** : c\'est lui qui a la barre la plus longue.',
                'Léa a 80 €, soit 20 € **de plus** que Tom : Tom en a moins, 80 − 20 = 60 €.',
            ],
            svg: SVG_PLUS_MOINS,
        },
    ],
};

// ═══════════════════════════════════════════════════════════
// Fiche 3 — Quelle question peut-on résoudre ?
// ═══════════════════════════════════════════════════════════

const BTN_E = 'cp900rejouerE', BTN_F = 'cp900rejouerF';

const puceOk = (x, y) =>
    `<circle cx="${x}" cy="${y}" r="7" fill="#dcfce7" stroke="#16a34a" stroke-width="1.4"/>` +
    `<path d="M ${x - 3.4} ${y} l 2.4 2.6 l 4.4 -5" fill="none" stroke="#16a34a" stroke-width="1.8" stroke-linecap="round"/>`;
const puceKo = (x, y) =>
    `<circle cx="${x}" cy="${y}" r="7" fill="#fee2e2" stroke="#dc2626" stroke-width="1.4"/>` +
    `<path d="M ${x - 3.2} ${y - 3.2} l 6.4 6.4 M ${x + 3.2} ${y - 3.2} l -6.4 6.4" fill="none" stroke="#dc2626" stroke-width="1.8" stroke-linecap="round"/>`;

const SVG_INVENTAIRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 182" width="290" height="176" role="img"
     aria-label="Un énoncé encadré indique qu'il lit 60 pages par jour pendant 30 jours. Deux grandeurs se cochent en vert : le nombre de pages par jour, 60, et le nombre de jours, 30. Deux autres se barrent en rouge : le temps de lecture et le prix du livre, que l'énoncé ne donne pas. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="19" font-size="13" font-weight="bold" fill="${ENCRE}">Ce que l'énoncé donne</text>
  ${bouton(BTN_E, 210, 4)}
  <rect x="14" y="30" width="272" height="34" rx="7" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.3"/>
  <text x="24" y="52" font-size="12" fill="${ARDOISE}">60 pages par jour pendant 30 jours.</text>
  ${etape(0.14, BTN_E, puceOk(26, 84) + `<text x="42" y="88" font-size="12" fill="${ARDOISE}">les pages par jour : <tspan font-weight="bold">60</tspan></text>`)}
  ${etape(0.32, BTN_E, puceOk(26, 110) + `<text x="42" y="114" font-size="12" fill="${ARDOISE}">le nombre de jours : <tspan font-weight="bold">30</tspan></text>`)}
  ${etape(0.54, BTN_E, puceKo(26, 138) + `<text x="42" y="142" font-size="12" fill="${GRIS}">le temps de lecture</text>`)}
  ${etape(0.72, BTN_E, puceKo(26, 164) + `<text x="42" y="168" font-size="12" fill="${GRIS}">le prix du livre</text>`)}
</svg>`.trim();

const caseQuestion = (y, texte, ok) =>
    `<rect x="14" y="${y}" width="272" height="32" rx="7" fill="${ok ? '#f0fdf4' : '#fef2f2'}" ` +
    `stroke="${ok ? '#16a34a' : '#dc2626'}" stroke-width="1.3"/>` +
    (ok ? puceOk(32, y + 16) : puceKo(32, y + 16)) +
    `<text x="50" y="${y + 20}" font-size="11.5" fill="${ARDOISE}">${texte}</text>`;

const SVG_TESTER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 162" width="290" height="157" role="img"
     aria-label="Deux questions sont mises à l'épreuve. Combien de pages en tout ? est acceptée : les deux nombres figurent dans l'énoncé, le calcul est 60 multiplié par 30. Combien de temps lit-il par jour ? est refusée : aucune durée n'est donnée, et aucun calcul ne peut l'inventer. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  <text x="14" y="19" font-size="13" font-weight="bold" fill="${ENCRE}">Tester chaque question</text>
  ${bouton(BTN_F, 210, 4)}
  ${etape(0.08, BTN_F, caseQuestion(32, 'Combien de pages en tout ?', true))}
  ${etape(0.30, BTN_F, `<text x="50" y="82" font-size="11.5" fill="#16a34a">les deux nombres sont là : 60 × 30</text>`)}
  ${etape(0.54, BTN_F, caseQuestion(96, 'Combien de temps lit-il par jour ?', false))}
  ${etape(0.76, BTN_F, `<text x="50" y="146" font-size="11.5" fill="#dc2626">aucune durée n'est donnée</text>`)}
</svg>`.trim();

/** Fiche « question » : inventorier les données, puis tester chaque question. */
const FICHE_QUESTION = {
    pages: [
        {
            titre: 'Ce que l\'énoncé donne',
            court: 'On ne calcule qu\'avec les nombres écrits dans l\'énoncé.',
            lignes: [
                '« 60 pages par jour pendant 30 jours » donne **60** et **30**.',
                'Aucune durée, aucun prix : ces grandeurs **n\'y sont pas**.',
            ],
            svg: SVG_INVENTAIRE,
        },
        {
            titre: 'Tester la question',
            court: 'Demande-toi avec quels nombres tu la calculerais.',
            lignes: [
                '« Combien de pages en tout ? » : 60 × 30. **Possible**.',
                '« Combien de temps par jour ? » : aucune durée. **Impossible**.',
            ],
            svg: SVG_TESTER,
        },
    ],
};

const CP_900 = {
    choisir_operation_qcm_unique:   FICHE_CHOISIR,
    choisir_operation_texte:        FICHE_CHOISIR,
    choisir_operation_expression:   FICHE_CHOISIR,
    choisir_operation_tableau:      FICHE_CHOISIR,
    // Campagne 5e (2026-09-21) : la carte « associer » ne porte plus que des
    // calculs à UNE opération sur les mêmes nombres (spec 900.json) ; son
    // ancienne fiche (répétitions, moitié) est retirée et la carte suit la
    // fiche « choisir », dont les quatre cas sont exactement son contenu.
    // Mode auto, À ARBITRER : une fiche dédiée reste possible.
    associer_calcul_relier:         FICHE_CHOISIR,
    associer_calcul_qcm_unique:     FICHE_CHOISIR,
    associer_calcul_texte:          FICHE_CHOISIR,
    question_resoluble_qcm_unique:  FICHE_QUESTION,
    question_resoluble_texte:       FICHE_QUESTION,
    // calcul_nu et division_reste (cartes ajoutées par la campagne 5e) : pas
    // encore de fiche, la sheet retombe sur la fiche « defaut ».
};

export default CP_900;
